import { z } from "zod";

/** Which section of the wizard's "Configure" step a placeholder is grouped under. */
export const TemplatePlaceholderGroupSchema = z.enum([
  "trigger",
  "calculation",
  "tiers",
  "split",
  "spiff",
  "guardrails",
  "automation",
]);

export const TemplatePlaceholderSchema = z.object({
  key: z.string(),
  label: z.string(),
  type: z.enum(["CRM_FIELD", "USER_SELECTOR", "NUMBER", "STRING", "CURRENCY", "BOOLEAN", "DATE"]),
  defaultValue: z.any().optional(),
  description: z.string().optional(),
  allowedTypes: z.array(z.string()).optional(),
  /** Wizard "Configure" step grouping. Defaults to "calculation" when absent. */
  group: TemplatePlaceholderGroupSchema.optional(),
});

export const TemplateComplexitySchema = z.enum(["SIMPLE", "STANDARD", "ADVANCED"]);

export const RuleTemplateSchema = z.object({
  id: z.string(),
  title: z.string(),
  subTitle: z.string(),
  description: z.string(),
  tags: z.array(z.string()),
  bestForCase: z.array(z.string()),
  commissionModal: z.string(), // E.g., 'TIERED', 'SPLIT', 'STRAIGHT'
  requiredPlan: z.enum(["FREE", "SALES_BOOSTER", "ENTERPRISE"]),
  supportedEntities: z.array(z.number()), // E.g., [2] for Deals, [31] for Invoices
  rulePayload: z.record(z.string(), z.any()), // The partial RuleCreateInput with placeholders

  /** Grouping facet for the gallery (e.g. "Real Estate", "SaaS & Technology"). */
  industry: z.string(),
  /** lucide-react icon name rendered on the card. */
  icon: z.string(),
  /** Signals how much configuration this template involves — feeds recommendations. */
  complexity: TemplateComplexitySchema,
  /** Punchy best-fit chips shown on the card and in the recommendation panel. */
  recommendedFor: z.array(z.string()),
  /** Honest "before you use this" caveats — when NOT to reach for this template. */
  watchOuts: z.array(z.string()),
  /** Human-readable bullets of every non-default config value this template sets. */
  configSummary: z.array(z.string()),
});

export type TemplatePlaceholderGroup = z.infer<typeof TemplatePlaceholderGroupSchema>;
export type TemplatePlaceholder = z.infer<typeof TemplatePlaceholderSchema>;
export type TemplateComplexity = z.infer<typeof TemplateComplexitySchema>;
export type RuleTemplate = z.infer<typeof RuleTemplateSchema>;
